<ion-header [translucent]="false">
  <ion-toolbar
    class="treatment-toolbar ion-no-border"
    [class.toolbar--welcome]="isWelcomePage"
    [class.toolbar--profile]="isProfilePage"
  >
    <!-- LEFT: menu (dev only; hidden on profile) -->
    @if (showSideMenuButton && !isProfilePage) {
      <ion-buttons slot="start">
        <ion-menu-button></ion-menu-button>
      </ion-buttons>
    }
    <!-- LEFT: title + two-line subtitle -->
    <div slot="start" class="left-title-wrap stacked">
      <span class="title">{{ title }}</span>
      @if (serialNumber) {
        <span class="serial-number">{{ serialNumberDisplay }}</span>
      }
    </div>
    <!-- CENTER: nav + tabs (landscape) or dropdowns (portrait/compact) -->
    <ion-title class="center-wrap">
      <div class="center-inner">
        <!-- NAV CLUSTER (always visible) -->
        <div class="visit-nav">
          <ion-buttons class="nav-btns">
            <ion-button
              class="nav-btn prev"
              fill="solid"
              color="primary"
              [class.invisible]="!navigationEnabled || navDisabledPrev"
              (click)="navigationEnabled && !navDisabledPrev ? prevClicked() : null"
            >
              <ion-icon name="arrow-back-outline"></ion-icon>
            </ion-button>
          </ion-buttons>
          <div class="nav-text">
            <div class="line1">{{ navLine1 }}</div>
            <div class="line2" [innerHTML]="navLine2"></div>
            @if (showCurrentVisitBadge) {
              <div class="current-visit-badge">{{ 'treatment-page.buttons.current' | translate }}</div>
            }
          </div>
          <ion-buttons class="nav-btns">
            <ion-button
              class="nav-btn next"
              fill="solid"
              color="primary"
              [class.invisible]="!navigationEnabled || navDisabledNext"
              (click)="navigationEnabled && !navDisabledNext ? nextClicked() : null"
            >
              <ion-icon name="arrow-forward-outline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </div>
        <!-- ===== LANDSCAPE (not compact): Tabs + Actions as buttons ===== -->
        @if (!isCompact) {
          <div class="tab-buttons">
            <ion-buttons>
              @for (page of menuPages; track page) {
                <ion-button
                  class="tab-btn"
                  [disabled]="page.isDisabled"
                  [class.active]="activePage === page"
                  (click)="pageSelect(page)"
                >
                  {{ page.title }}
                </ion-button>
              }
            </ion-buttons>
          </div>
          @if (statusBanner) {
            <div class="tab-buttons">
              <div class="status-banner" role="status">{{ statusBanner }}</div>
            </div>
          } @else if (actionButtons && actionButtons.length) {
            <div class="tab-buttons">
              <ion-buttons slot="end" class="actions-group">
                @for (button of actionButtons; track button) {
                  <ion-button class="action-button" (click)="actionButtonClick(button)">
                    {{ button.title }}
                  </ion-button>
                }
              </ion-buttons>
            </div>
          }
        }
        <!-- ===== PORTRAIT / COMPACT: Pages + Actions as dropdowns ===== -->
        @if (isCompact && menuPages && menuPages.length) {
          <div class="dropdown-row">
            <!-- Pages dropdown -->
            <ion-item lines="none" class="dropdown-item">
              <ion-select
                interface="popover"
                placeholder="{{ 'header-component.pages-placeholder' | translate }}"
                [value]="activePage?.title"
                [disabled]="areMenuPagesDisabled"
                (ionChange)="onPageDropdown($event.detail.value)"
              >
                @for (page of menuPages; track page) {
                  <ion-select-option [value]="page.title" [disabled]="page.isDisabled">{{ page.title }}</ion-select-option>
                }
              </ion-select>
            </ion-item>
            @if (statusBanner) {
              <div class="status-banner" role="status">{{ statusBanner }}</div>
            } @else if (actionButtons && actionButtons.length) {
              <ion-item lines="none" class="dropdown-item">
                <ion-select
                  interface="popover"
                  placeholder="{{ 'header-component.actions-placeholder' | translate }}"
                  (ionChange)="onActionDropdown($event.detail.value)"
                >
                  @for (button of actionButtons; track button) {
                    <ion-select-option [value]="button.title">{{ button.title }}</ion-select-option>
                  }
                </ion-select>
              </ion-item>
            }
          </div>
        }
      </div>
    </ion-title>

    <!-- Select Facility -->
    @if (isWelcomePage) {
      <div class="select-facility-dropdown" slot="end">
        <ion-select
          placeholder="{{ 'header-component.select' | translate }}"
          interface="popover"
          class="custom-input"
          [(ngModel)]="selectedFacility"
          fill="outline"
          (ionChange)="onFacilityChange($event)"
          required
        >
          @for (f of facilities; track f) {
            <ion-select-option [value]="f">{{ f.name }}</ion-select-option>
          }
        </ion-select>
      </div>
    }
    @if (isTreatmentPage || onClose) {
      <ion-buttons slot="end" class="close-group">
        @if (isTreatmentPage && practitionerService.facilityActive && allowConnectButton && showAdjustDeviceControls) {
          <img class="iqconnect-img" src="..\..\assets\images\iq-device-icon.png" />
          @if (deviceConnected) {
            <ion-button class="iqconnect-btn" fill="solid" color="primary" (click)="disconnect()">
              <ion-icon slot="start" name="bluetooth-outline"></ion-icon>
              {{ 'iq-connect-component.disconnect' | translate }}
            </ion-button>
          }
          @if (!deviceConnected) {
            <ion-button class="iqconnect-btn" fill="solid" color="primary" (click)="connect()">
              <ion-icon slot="start" name="bluetooth-outline"></ion-icon>
              {{ 'iq-connect-component.connect' | translate }}
            </ion-button>
          }
          <ion-button
            class="iqconnect-btn"
            fill="solid"
            color="primary"
            (click)="onTreatmentModeChanged()"
          >
            Mode: {{ adjustmentModeText }}
          </ion-button>
        }
        @if (onClose) {
          <ion-button class="action-button" (click)="closeClicked()">
            {{ 'header-component.close' | translate }}
          </ion-button>
        }
      </ion-buttons>
    }
    <div class="network-status-container">
      <div *ngIf="isOnline$ | async; else offlineTemplate">
        <ion-icon class="network-status-online" name="logo-rss"></ion-icon>
      </div>
      <ng-template #offlineTemplate>
        <ion-icon class="network-status-offline" name="logo-rss"></ion-icon>
      </ng-template>
      @if (showDeviceSessionMessages && deviceConnected) {
        @if (deviceMessagesCount$ | async; as messageCount) {
          @if (messageCount > 0) {
            <ion-button class="device-messages-btn" fill="clear" (click)="openDeviceMessages($event)">
              <ion-icon name="warning-outline" class="device-messages-icon"></ion-icon>
              @if (messageCount > 1) {
                <span class="device-messages-badge"></span>
              }
            </ion-button>
          }
        }
      }
    </div>
  </ion-toolbar>
</ion-header>
<app-iq-connect-device
  #iqDevice
  [onlyRenderDisconnect]="true"
  [scanOnStartup]="false"
  (onSerialNumber)="handleSerial($event)"
  (onDisplayBoardFirmwareVersion)="handleDisplayBoardFirmwareVersion($event)"
  (onMainBoardFirmwareVersion)="handleMainBoardFirmwareVersion($event)"
  (onConnected)="handleConnected($event)"
  (onDisconnected)="handleDisconnected($event)"
>
</app-iq-connect-device>
